import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';

const defaultPermissions = {
  canPost: false,
  canEditOwn: false,
  canEditAll: false,
  canDelete: false,
  canModerate: false,
};

const GroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentGroup, setParentGroup] = useState('');
  const [permissions, setPermissions] = useState({ ...defaultPermissions });
  const [message, setMessage] = useState(null);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = () => {
    axiosInstance.get('/groups').then(res => setGroups(res.data)).catch(() => setGroups([]));
  };

  const handleAddGroup = (e) => {
    e.preventDefault();
    axiosInstance.post('/groups', { name, description, parentGroup: parentGroup || null, permissions })
      .then(() => {
        setMessage({ type: 'success', text: 'Gruppe erfolgreich erstellt!' });
        setName('');
        setDescription('');
        setParentGroup('');
        setPermissions({ ...defaultPermissions });
        fetchGroups();
      }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Erstellen.' }));
  };

  const handleEdit = (group) => {
    setEditGroupId(group._id);
    setEditData({
      name: group.name,
      description: group.description,
      parentGroup: group.parentGroup?._id || '',
      permissions: group.permissions || { ...defaultPermissions },
    });
  };

  const handleUpdateGroup = (groupId) => {
    axiosInstance.put(`/groups/${groupId}`, editData)
      .then(() => {
        setMessage({ type: 'success', text: 'Gruppe aktualisiert!' });
        setEditGroupId(null);
        fetchGroups();
      }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Aktualisieren.' }));
  };

  const confirmDelete = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const handleDeleteGroup = () => {
    axiosInstance.delete(`/groups/${groupToDelete._id}`)
      .then(() => {
        setMessage({ type: 'success', text: 'Gruppe gelöscht!' });
        fetchGroups();
      }).catch(() => setMessage({ type: 'error', text: 'Fehler beim Löschen.' }))
      .finally(() => {
        setShowDeleteModal(false);
        setGroupToDelete(null);
      });
  };

  const handlePermissionChange = (key, value, editing = false) => {
    if (editing) {
      setEditData(prev => ({ ...prev, permissions: { ...prev.permissions, [key]: value } }));
    } else {
      setPermissions(prev => ({ ...prev, [key]: value }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Gruppen-Verwaltung</h2>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          {message.text}
        </div>
      )}

      <h4 className="text-lg font-semibold mb-3">Bestehende Gruppen</h4>
      {groups.map(group => (
        <div key={group._id} className="card bg-base-100 shadow mb-4">
          <div className="card-body">
            {editGroupId === group._id ? (
              <>
                <input className="input input-bordered mb-2" value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))} required />
                <textarea className="textarea textarea-bordered mb-2" value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))} />
                <select className="select select-bordered mb-2" value={editData.parentGroup}
                  onChange={(e) => setEditData(prev => ({ ...prev, parentGroup: e.target.value }))}>
                  <option value="">Keine</option>
                  {groups.filter(g => g._id !== group._id).map(g => (
                    <option key={g._id} value={g._id}>{g.name}</option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {Object.keys(defaultPermissions).map((key) => (
                    <label key={key} className="label cursor-pointer">
                      <span className="label-text">{key}</span>
                      <input type="checkbox" className="toggle"
                        checked={editData.permissions?.[key] || false}
                        onChange={(e) => handlePermissionChange(key, e.target.checked, true)} />
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-success btn-sm" onClick={() => handleUpdateGroup(group._id)}>💾 Speichern</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditGroupId(null)}>❌ Abbrechen</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-bold">{group.name}</h3>
                <p>{group.description}</p>
                {group.parentGroup && <p className="text-sm text-gray-500">Parent: {group.parentGroup.name}</p>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {Object.keys(defaultPermissions).map((key) => (
                    <div key={key} className={`badge ${group.permissions?.[key] ? 'badge-success' : 'badge-ghost'}`}>
                      {key}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <button className="btn btn-outline btn-sm" onClick={() => handleEdit(group)}>✏️ Bearbeiten</button>
                  <button className="btn btn-outline btn-error btn-sm" onClick={() => confirmDelete(group)}>🗑️ Löschen</button>
                </div>
              </>
            )}
          </div>
        </div>
      ))}

      <h4 className="text-lg font-semibold mt-6 mb-3">Neue Gruppe erstellen</h4>
      <form onSubmit={handleAddGroup} className="card bg-base-100 shadow p-4 mb-6">
        <input className="input input-bordered mb-2" type="text" placeholder="Name"
          value={name} onChange={(e) => setName(e.target.value)} required />
        <textarea className="textarea textarea-bordered mb-2" placeholder="Beschreibung"
          value={description} onChange={(e) => setDescription(e.target.value)} />
        <select className="select select-bordered mb-2" value={parentGroup}
          onChange={(e) => setParentGroup(e.target.value)}>
          <option value="">Keine</option>
          {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2 mb-2">
          {Object.keys(defaultPermissions).map((key) => (
            <label key={key} className="label cursor-pointer">
              <span className="label-text">{key}</span>
              <input type="checkbox" className="toggle"
                checked={permissions[key]}
                onChange={(e) => handlePermissionChange(key, e.target.checked)} />
            </label>
          ))}
        </div>
        <button className="btn btn-primary w-full">Gruppe erstellen</button>
      </form>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Gruppe löschen</h3>
            <p className="py-4">Bist du sicher, dass du <strong>{groupToDelete?.name}</strong> löschen möchtest?</p>
            <div className="modal-action">
              <button className="btn" onClick={() => setShowDeleteModal(false)}>Abbrechen</button>
              <button className="btn btn-error" onClick={handleDeleteGroup}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
